<template>
    <Head title="Journeys" />
    <div class="journeys-page-utilities absolute top-4 right-4 z-10 flex items-center gap-1.5 sm:top-5 sm:right-6 sm:gap-3">
        <BaseButton
            v-if="timezone"
            type="button"
            data-testid="development-journeys-timezone"
            theme="neutral"
            size="sm"
            class="inline-flex h-8 items-center gap-1.5 border-transparent bg-transparent px-1.5 text-ink-muted shadow-none hover:bg-surface-sunken"
            :aria-label="`Change time zone (currently ${formatTimezoneLabel(timezone)})`"
            @click="openTimezoneDialog">
            <span class="details">{{ formatTimezoneLabel(timezone) }}</span>
            <i class="bi bi-chevron-down text-[10px]" aria-hidden="true"></i>
        </BaseButton>
        <DevelopmentJourneysFeedbackDialog
            v-model:open="isPageFeedbackOpen"
            :is-submitting="isSubmittingPageFeedback"
            :error-message="pageFeedbackError"
            @submit="submitPageFeedback">
            <template #trigger>
                <BaseButton
                    type="button"
                    theme="neutral"
                    size="sm"
                    class="inline-flex h-8 items-center gap-1.5 rounded-full px-3 shadow-none"
                    data-testid="open-development-journeys-feedback"
                    @click="clearPageFeedbackError">
                    <i class="bi bi-chat-left text-[14px]" aria-hidden="true"></i>
                    <span class="details">Feedback</span>
                </BaseButton>
            </template>
        </DevelopmentJourneysFeedbackDialog>
    </div>
    <BaseLayout gap="none" fill-height class="development-journeys-page lg:min-h-0 lg:overflow-hidden" content-class="!pb-5 lg:min-h-0 lg:overflow-hidden">
        <BasePageHeader title="Development Journeys" class="!my-4 shrink-0 max-sm:!mt-14" />

        <main v-if="isTimezoneRequired" class="flex w-full flex-1 lg:min-h-0" data-testid="development-journeys-timezone-empty-state">
            <BaseCard
                as="section"
                padding="none"
                radius="lgr"
                class="flex min-h-[30rem] w-full flex-1 items-center justify-center !border-[#E7E2DB] lg:min-h-0"
                aria-labelledby="timezone-required-title">
                <div class="flex max-w-sm flex-col items-center gap-4 px-6 py-12 text-center">
                    <span class="flex size-12 items-center justify-center rounded-full bg-[#F0EDE7] text-[#1E6461]" aria-hidden="true">
                        <i class="bi bi-clock" aria-hidden="true"></i>
                    </span>
                    <div class="flex flex-col gap-2">
                        <h2 id="timezone-required-title" class="text-xl leading-6 font-medium text-[#3A3631] font-sans">Set your time zone</h2>
                        <p class="text-sm leading-5 text-[#6E6860] font-sans">Set your time zone to get the full Development Journeys experience.</p>
                    </div>
                    <BaseButton
                        data-testid="set-development-journeys-timezone"
                        theme="primary"
                        class="min-h-10 rounded-lg bg-valence-stone-850 hover:bg-valence-stone-900 disabled:bg-valence-cream-300 disabled:text-valence-cream-400"
                        @click="openTimezoneDialog">
                        Set time zone
                    </BaseButton>
                </div>
            </BaseCard>
        </main>

        <main v-else class="flex w-full flex-1 lg:min-h-0 lg:overflow-hidden" data-testid="development-journeys-layout">
            <div class="grid w-full flex-1 grid-cols-1 gap-5 lg:min-h-0 lg:overflow-hidden lg:grid-cols-[12.5rem_minmax(0,1fr)]">
                <BaseCard
                    as="section"
                    data-testid="development-goals-panel"
                    padding="none"
                    radius="lgr"
                    class="self-start flex min-w-0 flex-col p-2.5 !border-[#E7E2DB]"
                    aria-label="Your development goals">
                    <nav class="flex flex-col gap-2.5" aria-label="Your development goals">
                        <button
                            v-for="goal in developmentGoals"
                            :key="goal.id"
                            :ref="(element) => setGoalButtonRef(goal.id, element)"
                            data-testid="development-goal-slot"
                            type="button"
                            class="flex min-w-0 items-start gap-2.5 rounded-md px-2.5 py-2.5 text-left text-sm leading-5 text-[#1E1919] font-scaffold transition-colors hover:bg-[#F7F5F2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#30A29D]"
                            :aria-label="`View details for ${goal.title}`"
                            @click="showGoalDetails(goal)">
                            <span class="mt-1.5 size-2 shrink-0 rounded-full bg-[#81DADA]" aria-hidden="true"></span>
                            <h3 class="line-clamp-2 min-w-0 flex-1">{{ goal.title }}</h3>
                        </button>
                        <button
                            v-if="developmentGoals.length === 0 || canStartAnotherGoal"
                            ref="addGoalButton"
                            :data-testid="developmentGoals.length === 0 ? 'add-development-goal' : 'add-second-development-goal'"
                            type="button"
                            class="flex h-11 items-center gap-2.5 rounded-lg px-3.5 text-left text-[15px] leading-5 text-[#6E6860] font-scaffold transition-colors hover:bg-[#F7F5F2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#30A29D] disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="isStartingChat || isOpeningGoalChat"
                            :aria-busy="isStartingChat || isOpeningGoalChat"
                            @click="startGoalChat">
                            <i class="bi bi-plus-lg text-sm" aria-hidden="true"></i>
                            {{ isStartingChat || isOpeningGoalChat ? "Opening Nadia…" : "Add a goal" }}
                        </button>
                    </nav>
                    <BaseNotification v-if="startError" theme="error" :text="startError" hide-dismiss />
                    <BaseNotification v-if="weeklyActionConversationError && !isActionDetailsOpen" theme="error" :text="weeklyActionConversationError" hide-dismiss />
                </BaseCard>

                <section ref="weeklyActionsSection" class="flex min-h-0 min-w-0 flex-col" aria-labelledby="weekly-actions-title">
                    <div class="mb-3 flex shrink-0 flex-wrap items-start justify-between gap-4 border-b border-[#E7E2DB] pb-4">
                        <div>
                            <h2
                                id="weekly-actions-title"
                                data-weekly-workflow-heading
                                tabindex="-1"
                                class="flex items-center gap-3 text-2xl leading-7 font-medium text-[#3A3631] font-sans">
                                <DevelopmentJourneyHourglass class="size-[22px]" />
                                {{ weeklyActionsTitle }}
                            </h2>
                        </div>
                        <span v-if="weeklyWorkflow?.phase === 'normal'" class="rounded-full bg-[#C6E9E9] px-3.5 py-[3px] text-sm leading-5 text-[#1E6461] font-scaffold">
                            {{ weeklyActionsProgressLabel }}
                        </span>
                        <button
                            v-else-if="weeklyWorkflow?.phase === 'review'"
                            data-testid="open-weekly-action-review"
                            type="button"
                            class="rounded-full bg-[#F0EDE7] px-3.5 py-[3px] text-sm leading-5 text-[#3A3631] font-scaffold transition-colors hover:bg-[#E7E2DB]"
                            @click="openWeeklyActionReview">
                            {{ reviewOutcomesProgressLabel }}
                        </button>
                        <span v-else-if="weeklyWorkflow?.phase === 'commitment'" class="rounded-full bg-[#F0EDE7] px-3.5 py-[3px] text-sm leading-5 text-[#3A3631] font-scaffold">
                            {{ commitmentProgressLabel }}
                        </span>
                    </div>
                    <p data-testid="weekly-actions-announcement" class="sr-only" aria-live="polite" aria-atomic="true">
                        {{ weeklyActionsAnnouncement }}
                    </p>

                    <BaseCard data-testid="weekly-workspace" padding="none" radius="lgr" class="flex h-[30rem] min-h-0 flex-1 flex-col overflow-hidden !border-[#E7E2DB] lg:h-auto">
                        <div
                            v-if="isLoadingWeeklyWorkflow"
                            data-testid="weekly-workflow-loading"
                            role="status"
                            class="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 body-small text-valence-grey-750">
                            <LoadingSpinner class="h-8 w-8 text-valence-primary-500" aria-hidden="true" />
                            <span>Loading your weekly actions…</span>
                        </div>

                        <div
                            v-else-if="weeklyWorkflowError"
                            data-testid="weekly-workflow-error"
                            role="alert"
                            class="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center">
                            <p class="body-small text-valence-grey-750">{{ weeklyWorkflowError }}</p>
                            <BaseButton theme="neutral" size="sm" @click="loadWeeklyWorkflow">Try again</BaseButton>
                        </div>

                        <template v-else-if="weeklyWorkflow?.phase === 'review' || weeklyWorkflow?.phase === 'commitment'">
                            <WeeklyActionCommitment
                                :actions="suggestedActions"
                                :role-play-setup-url="role_play_setup_url"
                                :week-range="actionWeekRange"
                                :is-submitting="isSubmittingWeeklyWorkflow"
                                :is-locked="weeklyWorkflow?.phase === 'review'"
                                @commit="commitWeeklyActions"
                                @update:committed-action-count="committedActionCount = $event"
                                @view-details="showActionDetails" />
                            <WeeklyActionReview
                                v-if="weeklyWorkflow?.phase === 'review'"
                                v-model:open="isWeeklyActionReviewOpen"
                                :actions="reviewActions"
                                :role-play-setup-url="role_play_setup_url"
                                :week-range="reviewWeekRange"
                                :timezone="timezone"
                                :is-submitting="isSubmittingWeeklyWorkflow"
                                @submit="submitWeeklyActionReview"
                                @update:reviewed-action-count="reviewedActionCount = $event"
                                @view-details="showActionDetails" />
                        </template>

                        <template v-else>
                            <div data-testid="week-context-tray" class="relative flex shrink-0 items-center px-4 py-4 sm:px-5">
                                <span class="flex min-w-0 items-center gap-2 text-sm leading-5 text-[#6E6860] font-scaffold" :class="{ 'pr-44': !isViewingCurrentWeek }">
                                    <i class="bi bi-calendar-week shrink-0" aria-hidden="true"></i>
                                    <span class="hidden sm:inline">{{ weekContextLabel }}</span>
                                    <span class="sm:hidden">{{ compactWeekContextLabel }}</span>
                                </span>
                                <button
                                    v-if="!isViewingCurrentWeek"
                                    data-testid="snap-to-current-week"
                                    type="button"
                                    class="absolute right-4 top-1/2 inline-flex h-8 -translate-y-1/2 items-center justify-center gap-1.5 rounded-lg border border-[#DAD4CB] bg-white px-3 text-sm leading-5 font-medium text-[#2A2723] font-sans transition-colors hover:bg-[#F7F5F2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#30A29D] sm:right-5"
                                    aria-label="Return to this week"
                                    @click="returnToCurrentWeek">
                                    <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                                    <span>Return to this week</span>
                                </button>
                            </div>

                            <div class="shrink-0 px-3 pb-4 sm:px-4">
                                <div class="grid grid-cols-[auto_minmax(0,1fr)_auto] items-stretch gap-1.5 sm:gap-2">
                                    <button
                                        ref="previousWeekButton"
                                        type="button"
                                        class="flex size-8 self-center items-center justify-center rounded-lg text-[#6E6860] transition-colors hover:bg-[#F0EDE7] hover:text-[#2A2723] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#30A29D]"
                                        aria-label="Previous week"
                                        @click="moveSelectedWeek(-1)">
                                        <i class="bi bi-chevron-left" aria-hidden="true"></i>
                                    </button>

                                    <div
                                        class="flex h-9 min-w-0 items-center justify-center rounded-full bg-[#F0EDE7] px-2 text-center text-sm leading-5 font-medium text-[#2A2723] font-scaffold sm:hidden">
                                        <span class="truncate">{{ formatWeekRange(selectedWeekStart) }}</span>
                                    </div>

                                    <div class="hidden min-w-0 grid-cols-3 gap-2 sm:grid" role="tablist" aria-label="Weeks" aria-orientation="horizontal">
                                        <button
                                            v-for="weekStart in visibleWeekStarts"
                                            :id="`week-tab-${getWeekKey(weekStart)}`"
                                            :key="getWeekKey(weekStart)"
                                            :ref="(element) => setWeekTabRef(weekStart, element)"
                                            type="button"
                                            role="tab"
                                            :aria-controls="weeklyActionsPanelId"
                                            :aria-selected="getWeekKey(weekStart) === selectedWeekKey"
                                            :aria-label="`Week of ${formatWeekRange(weekStart)}${getWeekKey(weekStart) === currentWeekKey ? ', current week' : ''}`"
                                            :tabindex="getWeekKey(weekStart) === selectedWeekKey ? 0 : -1"
                                            class="flex h-9 min-w-0 items-center justify-center rounded-full px-2 text-center text-sm leading-5 font-scaffold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#30A29D]"
                                            :class="
                                                getWeekKey(weekStart) === selectedWeekKey
                                                    ? 'bg-[#F0EDE7] font-medium text-[#2A2723]'
                                                    : getWeekKey(weekStart) === currentWeekKey
                                                      ? 'text-[#6E6860] hover:bg-[#F7F5F2] hover:text-[#2A2723]'
                                                      : 'text-[#6E6860] hover:bg-[#F7F5F2] hover:text-[#2A2723]'
                                            "
                                            @click="selectWeek(weekStart, 'week_tab')"
                                            @keydown="onWeekTabKeydown($event, weekStart)">
                                            <span class="truncate">{{ formatCompactWeekRange(weekStart) }}</span>
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        class="flex size-8 self-center items-center justify-center rounded-lg text-[#6E6860] transition-colors hover:bg-[#F0EDE7] hover:text-[#2A2723] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#30A29D]"
                                        aria-label="Next week"
                                        @click="moveSelectedWeek(1)">
                                        <i class="bi bi-chevron-right" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </div>

                            <div
                                :id="weeklyActionsPanelId"
                                role="tabpanel"
                                :aria-labelledby="`week-tab-${selectedWeekKey}`"
                                :aria-label="`Weekly actions for ${formatWeekRange(selectedWeekStart)}`"
                                class="m-3 mt-0 flex flex-1 flex-col overflow-y-auto rounded-xl bg-[#F7F5F2]">
                                <div
                                    v-if="isLoadingWeeklyActions"
                                    data-testid="weekly-actions-loading"
                                    role="status"
                                    class="flex flex-1 items-center justify-center px-6 py-12 body-small text-valence-grey-750">
                                    Loading weekly actions…
                                </div>

                                <div
                                    v-else-if="weeklyActionsError"
                                    data-testid="weekly-actions-error"
                                    role="alert"
                                    class="flex flex-1 items-center justify-center px-6 py-12 text-center">
                                    <p class="body-small text-valence-grey-750">{{ weeklyActionsError }}</p>
                                </div>

                                <div
                                    v-if="actionStatusError"
                                    data-testid="weekly-action-status-error"
                                    role="alert"
                                    class="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 body-small text-red-800 sm:mx-5">
                                    {{ actionStatusError }}
                                </div>

                                <WeeklyActionList
                                    v-if="!isLoadingWeeklyActions && !weeklyActionsError && weeklyActions.length > 0"
                                    :actions="weeklyActions"
                                    :role-play-setup-url="role_play_setup_url"
                                    :is-current-week="isViewingCurrentWeek"
                                    :timezone="timezone"
                                    :completed-section-title="completedActionsTitle"
                                    :updating-action-ids="updatingActionIds"
                                    :is-conversation-launching="isWeeklyActionConversationPending"
                                    @update-status="updateWeeklyActionStatus"
                                    @view-details="showActionDetails"
                                    @start-conversation="startWeeklyActionConversation"
                                    @discuss-action="startActionDiscussion" />

                                <div
                                    v-else-if="!isLoadingWeeklyActions && !weeklyActionsError"
                                    data-testid="weekly-actions-empty-state"
                                    class="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 text-center">
                                    <div class="flex size-12 items-center justify-center rounded-full bg-white text-valence-primary-700 shadow-sm">
                                        <i class="bi bi-calendar3" aria-hidden="true"></i>
                                    </div>
                                    <div class="flex max-w-sm flex-col gap-1">
                                        <h3 class="details font-medium text-valence-grey-850">
                                            {{ isViewingPastWeek ? "No weekly actions were generated" : "Weekly actions will appear here" }}
                                        </h3>
                                        <p class="body-small text-valence-grey-750">
                                            {{
                                                isViewingPastWeek
                                                    ? `There were no weekly actions generated for ${formatWeekRange(selectedWeekStart)}.`
                                                    : `There are no weekly actions to show for ${formatWeekRange(selectedWeekStart)} yet.`
                                            }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </BaseCard>
                </section>
            </div>
        </main>

        <TimezoneRequiredDialog
            v-if="isTimezoneDialogOpen"
            :open="isTimezoneDialogOpen"
            :timezone="timezone"
            :timezones="timezone_options"
            @saved="reloadWithSavedTimezone"
            @update:open="updateTimezoneDialogOpen" />
        <WeeklyActionDetailsDialog
            v-if="selectedActionForDetails"
            v-model:open="isActionDetailsOpen"
            :action="selectedActionForDetails"
            :role-play-setup-url="role_play_setup_url"
            :show-primary-action="weeklyWorkflow?.phase === 'normal'"
            :is-conversation-launching="isWeeklyActionConversationPending"
            :conversation-error="weeklyActionConversationError"
            @start-conversation="startWeeklyActionConversation" />
        <DevelopmentGoalDetailsDialog
            v-if="selectedGoalForDetails"
            v-model:open="isGoalDetailsOpen"
            :goal="selectedGoalForDetails"
            :timezone="timezone"
            @deleted="handleGoalDeleted"
            @updated="handleGoalUpdated" />
        <DevelopmentActionFeedbackDialog
            v-if="selectedActionForFeedback"
            :open="isActionFeedbackOpen"
            :action-title="selectedActionForFeedback.title"
            :is-submitting="isSubmittingActionFeedback"
            :error-message="actionFeedbackError"
            @update:open="updateActionFeedbackOpen"
            @submit="submitActionFeedback" />
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";

export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { fetchApi, useFetch } from "~vue/api";
import type {
    CommitDevelopmentActionsRequest,
    DevelopmentActionListResponse,
    DevelopmentActionSuggestionListResponse,
    DevelopmentGoal,
    DevelopmentJourneysWeeklyWorkflow,
    SubmitDevelopmentActionReviewRequest,
    UpdateDevelopmentActionStatusEndpointV1DevelopmentJourneysActionsActionIdPatchResponse,
    UpdateDevelopmentActionStatusRequest,
} from "~vue/api/generated/development-journeys";
import type {
    DevelopmentActionFeedbackPayload,
    DevelopmentActionFeedbackRequest,
    DevelopmentJourneysFeedbackPayload,
    DevelopmentJourneysFeedbackRequest,
} from "~vue/api/generated/feedback";
import { dismissNotificationLocallyKey } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import { MainMenuPane } from "~vue/components/navigation/menuPanes";
import DevelopmentJourneyHourglass from "~vue/icons/DevelopmentJourneyHourglass.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import DevelopmentActionFeedbackDialog from "~vue/pages/developmentJourneys/DevelopmentActionFeedbackDialog.vue";
import DevelopmentGoalDetailsDialog from "~vue/pages/developmentJourneys/DevelopmentGoalDetailsDialog.vue";
import DevelopmentJourneysFeedbackDialog from "~vue/pages/developmentJourneys/DevelopmentJourneysFeedbackDialog.vue";
import { formatTimezoneLabel } from "~vue/pages/developmentJourneys/timezonePresentation";
import TimezoneRequiredDialog from "~vue/pages/developmentJourneys/TimezoneRequiredDialog.vue";
import WeeklyActionCommitment from "~vue/pages/developmentJourneys/WeeklyActionCommitment.vue";
import WeeklyActionDetailsDialog from "~vue/pages/developmentJourneys/WeeklyActionDetailsDialog.vue";
import WeeklyActionList from "~vue/pages/developmentJourneys/WeeklyActionList.vue";
import WeeklyActionReview from "~vue/pages/developmentJourneys/WeeklyActionReview.vue";
import type { WeeklyAction, WeeklyActionStatus } from "~vue/pages/developmentJourneys/weeklyActions";
import { addWeeks, formatCompactWeekRange, formatWeekRange, getWeekKey, getWeekStart, parseCalendarDate } from "~vue/pages/developmentJourneys/weekNavigation";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type ComponentPublicInstance, computed, inject, nextTick, onMounted, ref, watch } from "vue";

type StartGoalChatResponse = { chat_url: string };
type StartActionDiscussionChatResponse = { chat_url: string };
type StartWeeklyActionConversationResponse = { redirect_url?: string; error?: string };
type DevelopmentJourneysEntryPoint = "email" | "in_app";

const DEVELOPMENT_JOURNEYS_EMAIL_KINDS = new Set(["onboarding", "first_actions_ready", "friday_review", "action_commitment_reminder", "monday_action_selection_reminder"]);

const developmentJourneysEntryAttribution = (): { development_journeys_entry_point: DevelopmentJourneysEntryPoint; development_journeys_email_kind: string } => {
    const parameters = new URLSearchParams(globalThis.location.search);
    const emailKind = parameters.get("email_kind");
    if (parameters.get("entry") === "email" && emailKind && DEVELOPMENT_JOURNEYS_EMAIL_KINDS.has(emailKind)) {
        return {
            development_journeys_entry_point: "email",
            development_journeys_email_kind: emailKind,
        };
    }
    return {
        development_journeys_entry_point: "in_app",
        development_journeys_email_kind: "not_applicable",
    };
};

const props = defineProps<{
    action_discussion_chat_start_url: string;
    goal_chat_start_url: string;
    role_play_setup_url: string;
    development_goals: DevelopmentGoal[];
    initial_date?: string;
    timezone?: string;
    timezone_options: string[];
}>();

const dismissNotificationLocally = inject(dismissNotificationLocallyKey, () => {});
onMounted(() => dismissNotificationLocally(MainMenuPane.DEVELOPMENT_JOURNEYS));

const developmentGoals = ref([...props.development_goals]);
const isTimezoneRequired = computed(() => !props.initial_date);
const isTimezoneDialogOpen = ref(false);
const timezoneDialogOpener = ref<HTMLButtonElement | null>(null);
const initialWeekStart = getWeekStart(props.initial_date ? parseCalendarDate(props.initial_date) : new Date());
const currentWeekStart = ref(initialWeekStart);
const currentWeekKey = computed(() => getWeekKey(currentWeekStart.value));
const selectedWeekStart = ref(initialWeekStart);
const selectedWeekKey = computed(() => getWeekKey(selectedWeekStart.value));
type WeekContext = "past" | "current" | "future";
type WeekNavigationAction = "previous_week" | "next_week" | "week_tab" | "return_to_current_week" | "first_visible_week" | "last_visible_week";
const visibleWeekStarts = computed(() => [-1, 0, 1].map((offset) => addWeeks(selectedWeekStart.value, offset)));
const weeklyActionsPanelId = "weekly-actions-panel";
const goalButtonRefs = new Map<string, HTMLButtonElement>();
const addGoalButton = ref<HTMLButtonElement | null>(null);
const weekTabRefs = new Map<string, HTMLButtonElement>();
const previousWeekButton = ref<HTMLButtonElement | null>(null);
const weeklyActionsSection = ref<HTMLElement | null>(null);
const weeklyActions = ref<WeeklyAction[]>([]);
const isLoadingWeeklyActions = ref(false);
const weeklyActionsError = ref<string | null>(null);
const actionStatusError = ref<string | null>(null);
const updatingActionIds = ref<string[]>([]);
const ACTION_STATUS_DEBOUNCE_MS = 400;
const actionStatusLastUpdatedAt = new Map<string, number>();
const selectedActionForDetails = ref<WeeklyAction | null>(null);
const isActionDetailsOpen = ref(false);
const selectedGoalForDetails = ref<DevelopmentGoal | null>(null);
const isGoalDetailsOpen = ref(false);
const isPageFeedbackOpen = ref(false);
const isSubmittingPageFeedback = ref(false);
const pageFeedbackError = ref<string | null>(null);
const selectedActionForFeedback = ref<WeeklyAction | null>(null);
const isActionFeedbackOpen = ref(false);
const isSubmittingActionFeedback = ref(false);
const actionFeedbackError = ref<string | null>(null);
const weeklyWorkflow = ref<DevelopmentJourneysWeeklyWorkflow | null>(null);
const reviewActions = ref<WeeklyAction[]>([]);
const reviewedActionCount = ref(0);
const suggestedActions = ref<WeeklyAction[]>([]);
const committedActionCount = ref(0);
const isLoadingWeeklyWorkflow = ref(true);
const weeklyWorkflowError = ref<string | null>(null);
const isSubmittingWeeklyWorkflow = ref(false);
const hasLoadedWeeklyWorkflow = ref(false);
const isWeeklyActionReviewOpen = ref(false);

const canStartAnotherGoal = computed(() => developmentGoals.value.length > 0 && developmentGoals.value.length < 2);
const isViewingCurrentWeek = computed(() => selectedWeekKey.value === currentWeekKey.value);
const isViewingPastWeek = computed(() => selectedWeekStart.value < currentWeekStart.value);
const isViewingLastWeek = computed(() => selectedWeekKey.value === getWeekKey(addWeeks(currentWeekStart.value, -1)));
const isViewingNextWeek = computed(() => selectedWeekKey.value === getWeekKey(addWeeks(currentWeekStart.value, 1)));
const getWeekContext = (weekStart: Date): WeekContext => {
    if (weekStart < currentWeekStart.value) {
        return "past";
    }
    if (weekStart > currentWeekStart.value) {
        return "future";
    }
    return "current";
};
const completedActionCount = computed(() => weeklyActions.value.filter((action) => action.status === "completed").length);
const weeklyActionsProgressLabel = computed(() => `${completedActionCount.value} of ${weeklyActions.value.length} complete`);
const reviewOutcomesProgressLabel = computed(() => `${reviewedActionCount.value} of ${reviewActions.value.length} outcomes recorded`);
const committedActionsProgressLabel = computed(() => `${committedActionCount.value} of ${suggestedActions.value.length} committed`);
const commitmentProgressLabel = computed(() => (committedActionCount.value === 0 ? "No commitments yet" : committedActionsProgressLabel.value));
const reviewWeekRange = computed(() => (weeklyWorkflow.value?.review_week_start ? formatCompactWeekRange(parseCalendarDate(weeklyWorkflow.value.review_week_start)) : "last week"));
const actionWeekRange = computed(() =>
    weeklyWorkflow.value ? formatCompactWeekRange(parseCalendarDate(weeklyWorkflow.value.current_week_start)) : formatCompactWeekRange(currentWeekStart.value),
);
const weeklyActionsTitle = computed(() =>
    weeklyWorkflow.value?.phase === "review" || weeklyWorkflow.value?.phase === "commitment" ? `Commit to actions for ${actionWeekRange.value}` : "Actions",
);
const completedActionsTitle = computed(() => (isViewingCurrentWeek.value ? "Done this week" : `Done ${formatCompactWeekRange(selectedWeekStart.value)}`));
const weekContextLabel = computed(() => {
    if (isViewingCurrentWeek.value) {
        return "Viewing current week";
    }
    if (isViewingLastWeek.value) {
        return "Viewing last week";
    }
    if (isViewingNextWeek.value) {
        return "Viewing next week";
    }
    return isViewingPastWeek.value ? "Viewing past week" : "Viewing future week";
});
const compactWeekContextLabel = computed(() => {
    if (isViewingCurrentWeek.value) {
        return "Current week";
    }
    if (isViewingLastWeek.value) {
        return "Last week";
    }
    if (isViewingNextWeek.value) {
        return "Next week";
    }
    return isViewingPastWeek.value ? "Past week" : "Future week";
});
const weeklyActionsAnnouncement = computed(() => {
    if (weeklyWorkflow.value?.phase === "review") {
        return "Review last week's actions before choosing your next actions.";
    }
    if (weeklyWorkflow.value?.phase === "commitment") {
        return "Choose your next weekly actions.";
    }
    return `Showing weekly actions for ${formatWeekRange(selectedWeekStart.value)}.`;
});

watch(
    () => weeklyWorkflow.value?.phase,
    (phase, previousPhase) => {
        if (phase === "review" && previousPhase !== "review") {
            isWeeklyActionReviewOpen.value = true;
        }
        if (phase !== "review") {
            isWeeklyActionReviewOpen.value = false;
        }
    },
);
const startError = ref<string | null>(null);
const isOpeningGoalChat = ref(false);
const actionDiscussionPayload = ref<{ action_id: string } | null>(null);
const weeklyActionConversationPayload = ref<{ chat_source: string; initial_message: string } | null>(null);
const weeklyActionConversationError = ref<string | null>(null);
const isWeeklyActionConversationLaunching = ref(false);
const {
    data: startGoalChatResponse,
    error: startGoalChatError,
    execute: executeStartGoalChat,
    isFetching: isStartingChat,
} = useFetch(props.goal_chat_start_url, {
    immediate: false,
})
    .post({})
    .json<StartGoalChatResponse>();
const {
    data: startActionDiscussionResponse,
    error: startActionDiscussionError,
    execute: executeStartActionDiscussion,
    isFetching: isStartingActionDiscussion,
} = useFetch(props.action_discussion_chat_start_url, { immediate: false }).post(actionDiscussionPayload).json<StartActionDiscussionChatResponse>();
const {
    data: startWeeklyActionConversationResponse,
    error: startWeeklyActionConversationError,
    execute: executeStartWeeklyActionConversation,
    isFetching: isStartingWeeklyActionConversation,
} = useFetch("/coach/start/", { immediate: false }).post(weeklyActionConversationPayload).json<StartWeeklyActionConversationResponse>();
const isWeeklyActionConversationPending = computed(() => isStartingActionDiscussion.value || isStartingWeeklyActionConversation.value || isWeeklyActionConversationLaunching.value);

const startGoalChat = async () => {
    if (isTimezoneRequired.value || isStartingChat.value || isOpeningGoalChat.value) {
        return;
    }

    isOpeningGoalChat.value = true;
    startError.value = null;

    await executeStartGoalChat();
    if (startGoalChatError.value || !startGoalChatResponse.value?.chat_url) {
        startError.value = "We couldn't start this conversation. Please try again.";
        isOpeningGoalChat.value = false;
        return;
    }

    router.visit(startGoalChatResponse.value.chat_url, {
        onCancel: () => {
            startError.value = "We couldn't open this conversation. Please try again.";
            isOpeningGoalChat.value = false;
        },
        onError: () => {
            startError.value = "We couldn't open this conversation. Please try again.";
            isOpeningGoalChat.value = false;
        },
    });
};

const startWeeklyActionConversation = async (initialUserMessage: string) => {
    if (isWeeklyActionConversationPending.value) {
        return;
    }

    isWeeklyActionConversationLaunching.value = true;
    weeklyActionConversationError.value = null;
    weeklyActionConversationPayload.value = {
        chat_source: "development_journeys",
        initial_message: initialUserMessage,
    };
    await executeStartWeeklyActionConversation();
    const redirectUrl = startWeeklyActionConversationResponse.value?.redirect_url;
    if (startWeeklyActionConversationError.value || !redirectUrl) {
        weeklyActionConversationError.value = startWeeklyActionConversationResponse.value?.error ?? "We couldn't start this conversation. Please try again.";
        isWeeklyActionConversationLaunching.value = false;
        return;
    }

    router.visit(redirectUrl, {
        onCancel: () => {
            weeklyActionConversationError.value = "We couldn't open this conversation. Please try again.";
            isWeeklyActionConversationLaunching.value = false;
        },
        onError: () => {
            weeklyActionConversationError.value = "We couldn't open this conversation. Please try again.";
            isWeeklyActionConversationLaunching.value = false;
        },
        onFinish: () => {
            isWeeklyActionConversationLaunching.value = false;
        },
    });
};

const startActionDiscussion = async (actionId: string) => {
    if (isWeeklyActionConversationPending.value) {
        return;
    }

    isWeeklyActionConversationLaunching.value = true;
    weeklyActionConversationError.value = null;
    actionDiscussionPayload.value = { action_id: actionId };
    await executeStartActionDiscussion();
    const chatUrl = startActionDiscussionResponse.value?.chat_url;
    if (startActionDiscussionError.value || !chatUrl) {
        weeklyActionConversationError.value = "We couldn't start this conversation. Please try again.";
        isWeeklyActionConversationLaunching.value = false;
        return;
    }

    router.visit(chatUrl, {
        onCancel: () => {
            weeklyActionConversationError.value = "We couldn't open this conversation. Please try again.";
            isWeeklyActionConversationLaunching.value = false;
        },
        onError: () => {
            weeklyActionConversationError.value = "We couldn't open this conversation. Please try again.";
            isWeeklyActionConversationLaunching.value = false;
        },
        onFinish: () => {
            isWeeklyActionConversationLaunching.value = false;
        },
    });
};

const setWeekTabRef = (weekStart: Date, element: Element | ComponentPublicInstance | null) => {
    const weekKey = getWeekKey(weekStart);
    if (element instanceof HTMLButtonElement) {
        weekTabRefs.set(weekKey, element);
        return;
    }
    weekTabRefs.delete(weekKey);
};

const focusWeekTab = async (weekStart: Date) => {
    await nextTick();

    if (typeof window !== "undefined" && typeof window.matchMedia === "function" && !window.matchMedia("(min-width: 640px)").matches) {
        previousWeekButton.value?.focus();
        return;
    }

    weekTabRefs.get(getWeekKey(weekStart))?.focus();
};

const selectWeek = (weekStart: Date, navigationAction?: WeekNavigationAction) => {
    selectedWeekStart.value = weekStart;
    if (navigationAction) {
        void logUserInteraction(AmplitudeEventNames.DEVELOPMENT_JOURNEYS_WEEK_VIEWED, {
            week_context: getWeekContext(weekStart),
            week_navigation_action: navigationAction,
        });
    }
};

const moveSelectedWeek = (amount: number) => {
    selectWeek(addWeeks(selectedWeekStart.value, amount), amount < 0 ? "previous_week" : "next_week");
};

const returnToCurrentWeek = async () => {
    selectWeek(currentWeekStart.value, "return_to_current_week");
    await focusWeekTab(currentWeekStart.value);
};

const focusWeeklyWorkflow = async () => {
    await nextTick();

    if (weeklyWorkflow.value?.phase === "normal") {
        await focusWeekTab(selectedWeekStart.value);
        return;
    }

    weeklyActionsSection.value?.querySelector<HTMLElement>("[data-weekly-workflow-heading]")?.focus();
};

const openWeeklyActionReview = () => {
    isWeeklyActionReviewOpen.value = true;
};

const showActionDetails = (actionId: string) => {
    const action = [...weeklyActions.value, ...reviewActions.value, ...suggestedActions.value].find((weeklyAction) => weeklyAction.id === actionId);
    if (!action) {
        return;
    }
    selectedActionForDetails.value = action;
    isActionDetailsOpen.value = true;
};

const showGoalDetails = (goal: DevelopmentGoal) => {
    selectedGoalForDetails.value = goal;
    isGoalDetailsOpen.value = true;
};

const setGoalButtonRef = (goalId: string, element: Element | ComponentPublicInstance | null) => {
    if (element instanceof HTMLButtonElement) {
        goalButtonRefs.set(goalId, element);
        return;
    }
    goalButtonRefs.delete(goalId);
};

const focusGoalAfterDeletion = async () => {
    await nextTick();
    const nextGoal = developmentGoals.value[0];
    (nextGoal ? goalButtonRefs.get(nextGoal.id) : addGoalButton.value)?.focus();
};

const handleGoalDeleted = async (goalId: string) => {
    developmentGoals.value = developmentGoals.value.filter((goal) => goal.id !== goalId);
    weeklyActions.value = weeklyActions.value.filter((action) => action.development_goal?.id !== goalId);
    reviewActions.value = reviewActions.value.filter((action) => action.development_goal?.id !== goalId);
    suggestedActions.value = suggestedActions.value.filter((action) => action.development_goal?.id !== goalId);
    selectedGoalForDetails.value = null;
    isGoalDetailsOpen.value = false;
    await loadWeeklyWorkflow();
    if (weeklyWorkflow.value?.phase === "normal") {
        await loadWeeklyActions(selectedWeekKey.value);
    }
    await focusGoalAfterDeletion();
};

const handleGoalUpdated = (updatedGoal: DevelopmentGoal) => {
    developmentGoals.value = developmentGoals.value.map((goal) => (goal.id === updatedGoal.id ? updatedGoal : goal));
    if (selectedGoalForDetails.value?.id === updatedGoal.id) {
        selectedGoalForDetails.value = updatedGoal;
    }
};

const clearPageFeedbackError = () => {
    pageFeedbackError.value = null;
};

const submitPageFeedback = async (payload: DevelopmentJourneysFeedbackPayload) => {
    if (isSubmittingPageFeedback.value) {
        return;
    }
    isSubmittingPageFeedback.value = true;
    pageFeedbackError.value = null;
    try {
        const body = {
            artifact_type: "development_journeys",
            artifact_ref_id: null,
            payload,
        } satisfies DevelopmentJourneysFeedbackRequest;
        const response = await fetchApi("/v1/feedback", { method: "POST", body: JSON.stringify(body) });
        if (!response.ok) {
            throw new Error("Unable to submit page feedback");
        }
        isPageFeedbackOpen.value = false;
    } catch {
        pageFeedbackError.value = "We couldn't submit your feedback. Please try again.";
    } finally {
        isSubmittingPageFeedback.value = false;
    }
};

const submitActionFeedback = async (payload: DevelopmentActionFeedbackPayload) => {
    const action = selectedActionForFeedback.value;
    if (!action || isSubmittingActionFeedback.value) {
        return;
    }
    isSubmittingActionFeedback.value = true;
    actionFeedbackError.value = null;
    try {
        const body = {
            artifact_type: "development_action",
            artifact_ref_id: action.id,
            payload,
        } satisfies DevelopmentActionFeedbackRequest;
        const response = await fetchApi("/v1/feedback", { method: "POST", body: JSON.stringify(body) });
        if (!response.ok) {
            throw new Error("Unable to submit action feedback");
        }
        closeActionFeedback(action.id);
    } catch {
        actionFeedbackError.value = "We couldn't submit your review. Please try again.";
    } finally {
        isSubmittingActionFeedback.value = false;
    }
};

const restoreActionFeedbackFocus = async (actionId: string) => {
    await nextTick();
    weeklyActionsSection.value?.querySelector<HTMLElement>(`[data-action-id="${actionId}"] [data-testid="weekly-action-status-menu"]`)?.focus();
};

const closeActionFeedback = (actionId: string) => {
    isActionFeedbackOpen.value = false;
    void restoreActionFeedbackFocus(actionId);
};

const updateActionFeedbackOpen = (isOpen: boolean) => {
    if (isOpen) {
        isActionFeedbackOpen.value = true;
        return;
    }

    const actionId = selectedActionForFeedback.value?.id;
    if (actionId) {
        closeActionFeedback(actionId);
        return;
    }

    isActionFeedbackOpen.value = false;
};
const WEEKLY_ACTION_READ_RETRY_DELAYS_MS = [500, 1000] as const;

const fetchWeeklyActionRead = async (path: string): Promise<Response> => {
    for (const retryDelay of WEEKLY_ACTION_READ_RETRY_DELAYS_MS) {
        try {
            const response = await fetchApi(path);
            if (response.ok || response.status < 500) {
                return response;
            }
        } catch {
            // Safe GET requests can briefly race a proxy or API restart.
        }

        await new Promise<void>((resolve) => setTimeout(resolve, retryDelay));
    }

    return fetchApi(path);
};

const loadWeeklyActions = async (weekStart: string): Promise<boolean> => {
    if (isTimezoneRequired.value) {
        return false;
    }

    isLoadingWeeklyActions.value = true;
    weeklyActionsError.value = null;
    actionStatusError.value = null;

    try {
        const response = await fetchWeeklyActionRead(`/v1/development-journeys/actions?week_start=${encodeURIComponent(weekStart)}`);
        if (!response.ok) {
            throw new Error("Unable to load weekly actions");
        }
        const payload = (await response.json()) as DevelopmentActionListResponse;
        if (selectedWeekKey.value === weekStart) {
            weeklyActions.value = payload.actions;
        }
        return true;
    } catch {
        if (selectedWeekKey.value === weekStart) {
            weeklyActions.value = [];
            weeklyActionsError.value = "We couldn't load this week's actions. Please try again.";
        }
        return false;
    } finally {
        if (selectedWeekKey.value === weekStart) {
            isLoadingWeeklyActions.value = false;
        }
    }
};

const getWeeklyActions = async (weekStart: string): Promise<WeeklyAction[]> => {
    if (isTimezoneRequired.value) {
        return [];
    }

    const response = await fetchWeeklyActionRead(`/v1/development-journeys/actions?week_start=${encodeURIComponent(weekStart)}`);
    if (!response.ok) {
        throw new Error("Unable to load weekly actions");
    }
    return ((await response.json()) as DevelopmentActionListResponse).actions;
};

const loadWeeklyWorkflow = async () => {
    if (isTimezoneRequired.value) {
        return;
    }

    isLoadingWeeklyWorkflow.value = true;
    weeklyWorkflowError.value = null;
    let phaseDataLoaded = true;

    try {
        const response = await fetchWeeklyActionRead("/v1/development-journeys/weekly-workflow");
        if (!response.ok) {
            throw new Error("Unable to load weekly workflow");
        }
        const workflow = (await response.json()) as DevelopmentJourneysWeeklyWorkflow;
        currentWeekStart.value = getWeekStart(parseCalendarDate(workflow.calendar_week_start));
        if (!hasLoadedWeeklyWorkflow.value) {
            selectedWeekStart.value = getWeekStart(parseCalendarDate(workflow.current_week_start));
            hasLoadedWeeklyWorkflow.value = true;
        }
        weeklyWorkflow.value = workflow;
        reviewActions.value = [];
        reviewedActionCount.value = 0;
        suggestedActions.value = [];
        committedActionCount.value = 0;

        if (workflow.phase === "normal") {
            phaseDataLoaded = await loadWeeklyActions(selectedWeekKey.value);
        }
        if (workflow.phase === "review" && workflow.review_week_start) {
            reviewActions.value = await getWeeklyActions(workflow.review_week_start);
            reviewedActionCount.value = reviewActions.value.filter((action) => action.status === "completed").length;
        }
        if (workflow.phase === "review" || workflow.phase === "commitment") {
            const suggestionsResponse = await fetchWeeklyActionRead("/v1/development-journeys/action-suggestions");
            if (!suggestionsResponse.ok) {
                throw new Error("Unable to load action suggestions");
            }
            suggestedActions.value = ((await suggestionsResponse.json()) as DevelopmentActionSuggestionListResponse).actions;
        }
    } catch {
        weeklyWorkflow.value = null;
        reviewActions.value = [];
        reviewedActionCount.value = 0;
        suggestedActions.value = [];
        committedActionCount.value = 0;
        weeklyWorkflowError.value = "We couldn't load your weekly action flow. Please try again.";
    } finally {
        isLoadingWeeklyWorkflow.value = false;
        if (weeklyWorkflow.value && phaseDataLoaded) {
            await nextTick();
            const visibleActionCount =
                weeklyWorkflow.value.phase === "normal"
                    ? weeklyActions.value.length
                    : weeklyWorkflow.value.phase === "review"
                      ? reviewActions.value.length
                      : suggestedActions.value.length;
            void logUserInteraction(AmplitudeEventNames.DEVELOPMENT_JOURNEYS_WEEKLY_WORKFLOW_VIEWED, {
                development_journeys_workflow_phase: weeklyWorkflow.value.phase,
                visible_action_count: visibleActionCount,
                ...developmentJourneysEntryAttribution(),
            });
        }
    }
};

const submitWeeklyActionReview = async (outcomes: SubmitDevelopmentActionReviewRequest["outcomes"]) => {
    if (isTimezoneRequired.value || isSubmittingWeeklyWorkflow.value) {
        return;
    }

    isSubmittingWeeklyWorkflow.value = true;
    weeklyWorkflowError.value = null;
    try {
        const response = await fetchApi("/v1/development-journeys/weekly-workflow/review", {
            method: "POST",
            body: JSON.stringify({ outcomes } satisfies SubmitDevelopmentActionReviewRequest),
        });
        if (!response.ok) {
            throw new Error("Unable to submit weekly review");
        }
        await loadWeeklyWorkflow();
        await focusWeeklyWorkflow();
    } catch {
        weeklyWorkflowError.value = "We couldn't save your review. Please try again.";
    } finally {
        isSubmittingWeeklyWorkflow.value = false;
    }
};

const commitWeeklyActions = async (actionIds: CommitDevelopmentActionsRequest["action_ids"]) => {
    if (isTimezoneRequired.value || isSubmittingWeeklyWorkflow.value) {
        return;
    }

    isSubmittingWeeklyWorkflow.value = true;
    weeklyWorkflowError.value = null;
    try {
        const response = await fetchApi("/v1/development-journeys/actions/commit", {
            method: "POST",
            body: JSON.stringify({ action_ids: actionIds } satisfies CommitDevelopmentActionsRequest),
        });
        if (!response.ok) {
            throw new Error("Unable to commit weekly actions");
        }
        const committedActions = (await response.json()) as DevelopmentActionListResponse;
        const committedWeekStart = committedActions.actions[0]?.start_date;
        selectedWeekStart.value = committedWeekStart ? getWeekStart(parseCalendarDate(committedWeekStart)) : currentWeekStart.value;
        await loadWeeklyWorkflow();
        await focusWeeklyWorkflow();
    } catch {
        weeklyWorkflowError.value = "We couldn't save your selected actions. Please try again.";
    } finally {
        isSubmittingWeeklyWorkflow.value = false;
    }
};

const updateWeeklyActionStatus = async (actionId: string, status: WeeklyActionStatus) => {
    const action = weeklyActions.value.find((weeklyAction) => weeklyAction.id === actionId);
    const now = Date.now();
    const lastUpdatedAt = actionStatusLastUpdatedAt.get(actionId) ?? 0;
    if (isTimezoneRequired.value || !action || updatingActionIds.value.includes(actionId) || now - lastUpdatedAt < ACTION_STATUS_DEBOUNCE_MS) {
        return;
    }

    const originalAction = action;
    actionStatusError.value = null;
    actionStatusLastUpdatedAt.set(actionId, now);
    updatingActionIds.value = [...updatingActionIds.value, actionId];
    weeklyActions.value = weeklyActions.value.map((weeklyAction) => (weeklyAction.id === actionId ? { ...weeklyAction, status } : weeklyAction));

    try {
        const body: UpdateDevelopmentActionStatusRequest = {
            status,
            viewed_week_start: selectedWeekKey.value,
        };
        const response = await fetchApi(`/v1/development-journeys/actions/${actionId}`, {
            method: "PATCH",
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            throw new Error("Unable to update weekly action");
        }
        const updatedAction = (await response.json()) as UpdateDevelopmentActionStatusEndpointV1DevelopmentJourneysActionsActionIdPatchResponse;
        weeklyActions.value = weeklyActions.value.map((weeklyAction) => (weeklyAction.id === actionId ? updatedAction : weeklyAction));
        if (status === "completed" && updatedAction.channel !== "action_review_reminder") {
            selectedActionForFeedback.value = updatedAction;
            actionFeedbackError.value = null;
            isActionFeedbackOpen.value = true;
        }
    } catch {
        weeklyActions.value = weeklyActions.value.map((weeklyAction) => (weeklyAction.id === actionId ? originalAction : weeklyAction));
        actionStatusError.value = "We couldn't update this action. Please try again.";
    } finally {
        updatingActionIds.value = updatingActionIds.value.filter((id) => id !== actionId);
    }
};

watch(selectedWeekKey, (weekStart) => {
    if (!isTimezoneRequired.value && weeklyWorkflow.value?.phase === "normal") {
        void loadWeeklyActions(weekStart);
    }
});

watch(isTimezoneRequired, (timezoneRequired) => {
    if (!timezoneRequired) {
        void loadWeeklyWorkflow();
    }
});

if (!isTimezoneRequired.value) {
    void loadWeeklyWorkflow();
}

const reloadWithSavedTimezone = () => {
    router.visit(globalThis.location.href, { preserveState: false });
};

const openTimezoneDialog = (event: MouseEvent) => {
    timezoneDialogOpener.value = event.currentTarget as HTMLButtonElement;
    isTimezoneDialogOpen.value = true;
};

const updateTimezoneDialogOpen = async (isOpen: boolean) => {
    isTimezoneDialogOpen.value = isOpen;
    if (!isOpen) {
        await nextTick();
        timezoneDialogOpener.value?.focus();
    }
};

watch(isActionDetailsOpen, (isOpen) => {
    if (!isOpen) {
        selectedActionForDetails.value = null;
    }
});

watch(isGoalDetailsOpen, (isOpen) => {
    if (!isOpen) {
        selectedGoalForDetails.value = null;
    }
});

watch(isActionFeedbackOpen, (isOpen) => {
    if (!isOpen) {
        selectedActionForFeedback.value = null;
        actionFeedbackError.value = null;
    }
});

const onWeekTabKeydown = async (event: KeyboardEvent, weekStart: Date) => {
    let nextWeekStart: Date | null = null;
    switch (event.key) {
        case "ArrowLeft":
        case "ArrowUp":
            nextWeekStart = addWeeks(weekStart, -1);
            break;
        case "ArrowRight":
        case "ArrowDown":
            nextWeekStart = addWeeks(weekStart, 1);
            break;
        case "Home":
            nextWeekStart = visibleWeekStarts.value[0];
            break;
        case "End":
            nextWeekStart = visibleWeekStarts.value[visibleWeekStarts.value.length - 1];
            break;
        default:
            return;
    }

    event.preventDefault();
    const navigationAction = event.key === "Home" ? "first_visible_week" : event.key === "End" ? "last_visible_week" : nextWeekStart < weekStart ? "previous_week" : "next_week";
    selectWeek(nextWeekStart, navigationAction);
    await focusWeekTab(nextWeekStart);
};
</script>

<style scoped>
.development-journeys-page :deep(.base-layout-gutters) {
    min-height: 0;
}
</style>
